import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useToast } from '@/shared/hooks/use-toast'
import {
  Mails,
  Mail,
  AlertCircle,
  FileBarChart,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  User,
  Send,
  Layers,
  Search,
  Code,
  X,
  Star,
  Paperclip,
  Database,
  MoreVertical,
  Pencil,
  RefreshCw,
  Check,
  Save,
  MessageSquare,
  Copy,
} from 'lucide-react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useCampanias } from '@/modules/cronograma/hooks/useCampanias'
import { useTeam } from '@/shared/context/TeamContext'
import { useFiltersData, useCategorizacion, useReporteByCampania, useUpdateCampania } from '@/modules/multichannel/hooks/useCampanias'
import { HtmlPreviewModal } from '@/modules/multichannel/components/HtmlPreviewModal'
import { WhatsAppPreviewModal } from '@/modules/multichannel/components/WhatsAppPreviewModal'
import { useReporteWhatsAppPorCampania } from '@/modules/cronograma/hooks/useReportes'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import type { Campania } from '@/modules/cronograma/types/campania'

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

/** Paleta de colores distinguibles para laboratorios (nombre-tono Tailwind) */
const LAB_COLORS = [
  'teal-500', 'blue-500', 'violet-500', 'orange-500', 'pink-500',
  'emerald-500', 'red-500', 'indigo-500', 'amber-500', 'cyan-500',
  'lime-500', 'fuchsia-500', 'sky-500', 'rose-500', 'purple-500',
]

/** Mapa nombre-tono → hex para inline styles que requieren hex */
const TAILWIND_HEX: Record<string, string> = {
  'slate-300': '#cbd5e1', 'slate-400': '#94a3b8', 'slate-500': '#64748b', 'slate-600': '#475569', 'slate-700': '#334155',
  'gray-300': '#d1d5db', 'gray-400': '#9ca3af', 'gray-500': '#6b7280', 'gray-600': '#4b5563', 'gray-700': '#374151',
  'red-300': '#fca5a5', 'red-400': '#f87171', 'red-500': '#ef4444', 'red-600': '#dc2626', 'red-700': '#b91c1c',
  'orange-300': '#fdba74', 'orange-400': '#fb923c', 'orange-500': '#f97316', 'orange-600': '#ea580c', 'orange-700': '#c2410c',
  'amber-300': '#fcd34d', 'amber-400': '#fbbf24', 'amber-500': '#f59e0b', 'amber-600': '#d97706', 'amber-700': '#b45309',
  'yellow-300': '#fde047', 'yellow-400': '#facc15', 'yellow-500': '#eab308', 'yellow-600': '#ca8a04', 'yellow-700': '#a16207',
  'lime-300': '#bef264', 'lime-400': '#a3e635', 'lime-500': '#84cc16', 'lime-600': '#65a30d', 'lime-700': '#4d7c0f',
  'green-300': '#86efac', 'green-400': '#4ade80', 'green-500': '#22c55e', 'green-600': '#16a34a', 'green-700': '#15803d',
  'emerald-300': '#6ee7b7', 'emerald-400': '#34d399', 'emerald-500': '#10b981', 'emerald-600': '#059669', 'emerald-700': '#047857',
  'teal-300': '#5eead4', 'teal-400': '#2dd4bf', 'teal-500': '#14b8a6', 'teal-600': '#0d9488', 'teal-700': '#0f766e',
  'cyan-300': '#67e8f9', 'cyan-400': '#22d3ee', 'cyan-500': '#06b6d4', 'cyan-600': '#0891b2', 'cyan-700': '#0e7490',
  'sky-300': '#7dd3fc', 'sky-400': '#38bdf8', 'sky-500': '#0ea5e9', 'sky-600': '#0284c7', 'sky-700': '#0369a1',
  'blue-300': '#93c5fd', 'blue-400': '#60a5fa', 'blue-500': '#3b82f6', 'blue-600': '#2563eb', 'blue-700': '#1d4ed8',
  'indigo-300': '#a5b4fc', 'indigo-400': '#818cf8', 'indigo-500': '#6366f1', 'indigo-600': '#4f46e5', 'indigo-700': '#4338ca',
  'violet-300': '#c4b5fd', 'violet-400': '#a78bfa', 'violet-500': '#8b5cf6', 'violet-600': '#7c3aed', 'violet-700': '#6d28d9',
  'purple-300': '#d8b4fe', 'purple-400': '#c084fc', 'purple-500': '#a855f7', 'purple-600': '#9333ea', 'purple-700': '#7e22ce',
  'fuchsia-300': '#f0abfc', 'fuchsia-400': '#e879f9', 'fuchsia-500': '#d946ef', 'fuchsia-600': '#c026d3', 'fuchsia-700': '#a21caf',
  'pink-300': '#f9a8d4', 'pink-400': '#f472b6', 'pink-500': '#ec4899', 'pink-600': '#db2777', 'pink-700': '#be185d',
  'rose-300': '#fda4af', 'rose-400': '#fb7185', 'rose-500': '#f43f5e', 'rose-600': '#e11d48', 'rose-700': '#be123c',
}

function getHex(twColor: string): string {
  return TAILWIND_HEX[twColor] || '#9ca3af'
}


/** Genera los 42 días (6 semanas) del grid del mes */
function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1)
  // Retroceder al lunes anterior (getDay: 0=dom, 1=lun... ajustar para semana empezando en lunes)
  let dayOfWeek = firstDay.getDay()
  if (dayOfWeek === 0) dayOfWeek = 7 // domingo = 7
  const startOfGrid = new Date(year, month, 1 - (dayOfWeek - 1))

  const days: Date[] = []
  for (let i = 0; i < 42; i++) {
    days.push(new Date(startOfGrid.getFullYear(), startOfGrid.getMonth(), startOfGrid.getDate() + i))
  }
  return days
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

const WHATSAPP_TIPOS = ['Whatsapp', 'Email + Whatsapp']

export function CalendarioPage() {
  useDocumentTitle('Calendario')
  const today = useMemo(() => new Date(), [])
  const [currentMonth, setCurrentMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1))
  const [selectedCampania, setSelectedCampania] = useState<Campania | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [htmlPreviewOpen, setHtmlPreviewOpen] = useState(false)
  const [waPreviewOpen, setWaPreviewOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterMarca, setFilterMarca] = useState<string>('all')
  const [filterEstadoEnvio, setFilterEstadoEnvio] = useState<string>('all')
  const [filterTipos, setFilterTipos] = useState<Set<string>>(new Set())
  const [estadosModalOpen, setEstadosModalOpen] = useState(false)
  const [estadosForm, setEstadosForm] = useState({ estado_contenido: '', estado_envio: '', estado_reporte: '' })
  const [duplicarDialogOpen, setDuplicarDialogOpen] = useState(false)

  const navigate = useNavigate()
  const { toast } = useToast()
  const updateCampania = useUpdateCampania()

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const { data: filtersData } = useFiltersData()

  const areaLabMarcaId = useMemo(() => {
    if (!isClientArea || !selectedArea?.laboratorio_nombre || !filtersData) return undefined
    const marca = filtersData.marcas_principales.find(
      (m) => m.nombre === selectedArea.laboratorio_nombre
    )
    return marca?.id
  }, [isClientArea, selectedArea?.laboratorio_nombre, filtersData])

  // Filtrar campañas del mes visible (+/- margen para celdas de meses adyacentes)
  const fechaDesde = useMemo(() => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), -6)
    return format(d, 'yyyy-MM-dd')
  }, [currentMonth])

  const fechaHasta = useMemo(() => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 13)
    return format(d, 'yyyy-MM-dd')
  }, [currentMonth])

  const { data, isLoading, isError } = useCampanias({
    page_size: 1000,
    marca_principal_id: areaLabMarcaId,
    fecha_desde: fechaDesde,
    fecha_hasta: fechaHasta,
  })

  // Preview del reporte para la campaña seleccionada
  const isWhatsApp = selectedCampania ? WHATSAPP_TIPOS.includes(selectedCampania.tipo) && selectedCampania.tipo !== 'Email + Whatsapp' : false
  const hasReporte = selectedCampania?.estado_reporte === 'Generado'

  const { data: reportePreview } = useReporteByCampania(
    selectedCampania?.id ?? 0,
    hasReporte && !isWhatsApp
  )
  const { data: reporteWhatsAppPreview } = useReporteWhatsAppPorCampania(
    hasReporte && isWhatsApp ? (selectedCampania?.id ?? null) : null,
    hasReporte && isWhatsApp
  )

  // Categorización: IDs de campañas con reporte generado + ID del reporte redirigido
  const campaniaIdsConReporte = useMemo(() => {
    const ids = data?.items?.filter(c => c.estado_reporte?.toLowerCase() === 'generado').map(c => c.id) || []
    // Incluir campania_id del reporte destino (si hay redirección)
    const redirectId = reportePreview?.campania_id ?? reporteWhatsAppPreview?.campania_id
    if (redirectId && !ids.includes(redirectId)) {
      ids.push(redirectId)
    }
    return ids
  }, [data, reportePreview?.campania_id, reporteWhatsAppPreview?.campania_id])
  const { data: categorizaciones } = useCategorizacion(campaniaIdsConReporte)

  // Filtrar campañas localmente
  const filteredItems = useMemo(() => {
    if (!data?.items) return []
    return data.items.filter((c) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        if (!c.nombre_campania.toLowerCase().includes(q) && !c.marca.toLowerCase().includes(q) && !c.target.toLowerCase().includes(q))
          return false
      }
      if (filterMarca !== 'all' && c.marca !== filterMarca) return false
      if (filterEstadoEnvio !== 'all' && c.estado_envio !== filterEstadoEnvio) return false
      if (filterTipos.size > 0 && !filterTipos.has(c.tipo)) return false
      return true
    })
  }, [data, searchQuery, filterMarca, filterEstadoEnvio, filterTipos])

  // Agrupar campañas por fecha
  const campaniasPorFecha = useMemo(() => {
    const map = new Map<string, Campania[]>()
    filteredItems.forEach((campania) => {
      if (!campania.fecha_publicacion) return
      const fecha = campania.fecha_publicacion.split('T')[0]
      const existing = map.get(fecha) || []
      map.set(fecha, [...existing, campania])
    })
    return map
  }, [filteredItems])

  // Mapa de color por laboratorio/marca (usa marca_color_nombre del API si existe, fallback a paleta)
  const labColorMap = useMemo(() => {
    if (!data?.items) return new Map<string, string>()
    const marcas = [...new Set(data.items.map((c) => c.marca).filter(Boolean))].sort()
    const map = new Map<string, string>()
    let fallbackIdx = 0
    marcas.forEach((marca) => {
      const campaniaConColor = data.items.find((c) => c.marca === marca && c.marca_color_nombre)
      if (campaniaConColor?.marca_color_nombre) {
        map.set(marca, `${campaniaConColor.marca_color_nombre}-${campaniaConColor.marca_color_tono || '500'}`)
      } else {
        map.set(marca, LAB_COLORS[fallbackIdx % LAB_COLORS.length])
        fallbackIdx++
      }
    })
    return map
  }, [data])

  // Días del grid
  const calendarDays = useMemo(
    () => getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth()),
    [currentMonth]
  )

  // Marcas únicas para el filtro (de todos los items, no filtrados)
  const marcasUnicas = useMemo(() => {
    if (!data?.items) return []
    return [...new Set(data.items.map((c) => c.marca).filter(Boolean))].sort()
  }, [data])

  // Estados de envío únicos para el filtro
  const estadosEnvioUnicos = useMemo(() => {
    if (!data?.items) return []
    return [...new Set(data.items.map((c) => c.estado_envio).filter(Boolean))].sort()
  }, [data])

  // Tipos únicos para el filtro
  const tiposUnicos = useMemo(() => {
    if (!data?.items) return []
    return [...new Set(data.items.map((c) => c.tipo).filter(Boolean))].sort()
  }, [data])

  // Stats del mes visible
  const stats = useMemo(() => {
    if (!filteredItems.length) return { total: 0, diasConCampanias: 0, programadas: 0 }
    const month = currentMonth.getMonth()
    const year = currentMonth.getFullYear()
    const delMes = filteredItems.filter((c) => {
      if (!c.fecha_publicacion) return false
      const d = new Date(c.fecha_publicacion.split('T')[0] + 'T12:00:00')
      return d.getMonth() === month && d.getFullYear() === year
    })
    const diasUnicos = new Set(delMes.map((c) => c.fecha_publicacion?.split('T')[0]))
    return {
      total: delMes.length,
      diasConCampanias: diasUnicos.size,
      programadas: delMes.filter((c) => c.estado_envio === 'Programado').length,
    }
  }, [filteredItems, currentMonth])

  // Navegación de meses
  const goPrev = useCallback(() => setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1)), [])
  const goNext = useCallback(() => setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1)), [])

  const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: es })

  const hasActiveFilters = searchQuery || filterMarca !== 'all' || filterEstadoEnvio !== 'all' || filterTipos.size > 0
  const clearFilters = () => {
    setSearchQuery('')
    setFilterMarca('all')
    setFilterEstadoEnvio('all')
    setFilterTipos(new Set())
  }

  const toggleTipo = (tipo: string) => {
    setFilterTipos((prev) => {
      const next = new Set(prev)
      if (next.has(tipo)) next.delete(tipo)
      else next.add(tipo)
      return next
    })
  }

  if (isError) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold">Error al cargar campañas</h3>
              <p className="text-muted-foreground">No se pudieron cargar las campañas programadas</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Mails className="h-6 w-6 text-black dark:text-white" />
          Calendario de Campañas
        </h1>
        {!isLoading && data && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span><strong className="text-foreground">{stats.total}</strong> campañas</span>
            <span><strong className="text-foreground">{stats.diasConCampanias}</strong> días</span>
            <span><strong className="text-foreground">{stats.programadas}</strong> programadas</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendario Grid */}
        <div className="lg:col-span-2">
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="p-4">
              {/* Barra de navegación + Leyenda */}
              <div className="flex items-center justify-between mb-4">
                {/* Leyenda de laboratorios */}
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {!isLoading && labColorMap.size > 0 && [...labColorMap.entries()].map(([marca, twColor]) => (
                    <div key={marca} className="flex items-center gap-1">
                      <div className={`w-2.5 h-2.5 rounded-sm shrink-0 bg-${twColor}`} />
                      <span className="text-[11px] text-muted-foreground">{marca}</span>
                    </div>
                  ))}
                </div>
                {/* Navegación */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goPrev}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="text-lg font-semibold capitalize">{monthLabel}</h2>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goNext}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <Skeleton className="h-[500px] w-full" />
              ) : (
                <>
                  {/* Header días de la semana */}
                  <div className="grid grid-cols-7 mb-1">
                    {DIAS_SEMANA.map((dia) => (
                      <div key={dia} className="text-center text-xs font-medium text-muted-foreground py-2">
                        {dia}
                      </div>
                    ))}
                  </div>

                  {/* Grid de celdas */}
                  <div className="grid grid-cols-7 border-t border-l border-gray-200 dark:border-gray-700">
                    {calendarDays.map((day, idx) => {
                      const dateKey = format(day, 'yyyy-MM-dd')
                      const campanias = campaniasPorFecha.get(dateKey) || []
                      const isCurrentMonth = day.getMonth() === currentMonth.getMonth()
                      const isToday = isSameDay(day, today)
                      const maxVisible = 3
                      const extras = campanias.length - maxVisible

                      return (
                        <div
                          key={idx}
                          className={`border-r border-b border-gray-200 dark:border-gray-700 min-h-[100px] p-1 ${
                            isCurrentMonth
                              ? 'bg-background'
                              : 'bg-gray-50 dark:bg-gray-800/30'
                          }`}
                        >
                          {/* Número del día */}
                          <div className="flex items-center gap-0.5 mb-0.5">
                              <span
                                className={`text-xs font-medium w-5 h-5 flex items-center justify-center ${
                                  isToday
                                    ? 'text-teal-600 dark:text-teal-400 font-bold'
                                    : isCurrentMonth
                                      ? 'text-foreground'
                                      : 'text-muted-foreground'
                                }`}
                              >
                              {day.getDate()}
                            </span>
                            {isToday && <div className="w-1 h-1 rounded-full bg-teal-500" />}
                          </div>

                          {/* Mini-cards de campañas */}
                          <div className="space-y-0.5">
                            {campanias.slice(0, maxVisible).map((campania) => (
                              <button
                                key={campania.id}
                                onClick={() => { setSelectedCampania(campania); setSelectedDate(null) }}
                                className={`w-full flex items-stretch rounded text-left transition-colors hover:opacity-80 ${
                                  selectedCampania?.id === campania.id
                                    ? 'ring-1'
                                    : ''
                                }`}
                                style={selectedCampania?.id === campania.id
                                  ? { '--tw-ring-color': getHex(labColorMap.get(campania.marca) || 'gray-400') } as React.CSSProperties
                                  : undefined
                                }
                              >
                                <div
                                  className={`w-1 shrink-0 rounded-l bg-${labColorMap.get(campania.marca) || 'gray-400'}`}
                                />
                                <div
                                  className={`flex-1 px-1 py-0.5 rounded-r truncate bg-${labColorMap.get(campania.marca) || 'gray-400'}/10`}
                                >
                                  <span className="text-[10px] leading-tight truncate block">
                                    {campania.nombre_campania}
                                  </span>
                                </div>
                              </button>
                            ))}
                            {extras > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedCampania(null)
                                  setSelectedDate(dateKey)
                                }}
                                className="text-[10px] text-teal-600 dark:text-teal-400 hover:underline px-1"
                              >
                                +{extras} más
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panel derecho - Filtros + Detalle */}
        <div className="lg:col-span-1 space-y-4">
          {/* Filtros */}
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardContent className="p-3 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Buscar campaña..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Select value={filterMarca} onValueChange={setFilterMarca}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Laboratorio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los labs</SelectItem>
                    {marcasUnicas.map((m) => (
                      <SelectItem key={m} value={m}>
                        <div className="flex items-center gap-1.5">
                          <div className={`h-2.5 w-2.5 rounded-full shrink-0 bg-${labColorMap.get(m) || 'gray-400'}`} />
                          {m}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterEstadoEnvio} onValueChange={setFilterEstadoEnvio}>
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {estadosEnvioUnicos.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {tiposUnicos.length > 0 && (
                <div className="space-y-1">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Tipo</span>
                  <div className="flex flex-wrap gap-1">
                    {tiposUnicos.map((tipo) => (
                      <button
                        key={tipo}
                        onClick={() => toggleTipo(tipo)}
                        className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                          filterTipos.has(tipo)
                            ? 'bg-teal-500 text-white border-teal-500'
                            : 'bg-transparent text-muted-foreground border-gray-300 dark:border-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {tipo}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {hasActiveFilters && (
                <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                  Limpiar filtros
                </button>
              )}
            </CardContent>
          </Card>

          {/* Aviso si falta asunto (no aplica para WhatsApp puro) */}
          {selectedCampania && !selectedCampania.asunto && !(WHATSAPP_TIPOS.includes(selectedCampania.tipo) && selectedCampania.tipo !== 'Email + Whatsapp') && (
            <Card className="border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="p-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-sm text-amber-700 dark:text-amber-400">Esta campaña no tiene asunto definido</span>
              </CardContent>
            </Card>
          )}

          {/* Simulación bandeja de entrada */}
          {selectedCampania?.asunto && (
            <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden">
              {/* Header bandeja */}
              <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-[11px] text-muted-foreground">Bandeja de entrada</span>
                </div>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-gray-300 dark:border-gray-600">
                  <Mail className="h-2.5 w-2.5 mr-0.5" /> 1
                </Badge>
              </div>
              {/* Email row */}
              <div className={`px-3 py-2 flex items-center gap-2 bg-white dark:bg-gray-900/50 border-l-2 border-l-${labColorMap.get(selectedCampania.marca) || 'gray-400'}`}
              >
                <Star className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-foreground truncate">{selectedCampania.remitente}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {selectedCampania.hora ? selectedCampania.hora.slice(0, 5) : 'Ahora'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-semibold text-foreground truncate">{selectedCampania.asunto}</span>
                    <Paperclip className="h-2.5 w-2.5 text-gray-400 shrink-0" />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Detalle */}
          <Card className="border border-gray-300 dark:border-gray-700 sticky top-4">
            <CardContent className="p-4">
              {selectedDate && !selectedCampania ? (
                /* Lista de campañas del día */
                (() => {
                  const campanias = campaniasPorFecha.get(selectedDate) || []
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-sm">
                          {format(new Date(selectedDate + 'T12:00:00'), "d 'de' MMMM", { locale: es })}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {campanias.length} campañas
                        </Badge>
                      </div>
                      <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                        {campanias.map((campania) => (
                          <button
                            key={campania.id}
                            onClick={() => { setSelectedCampania(campania); setSelectedDate(null) }}
                            className="w-full flex items-stretch rounded text-left transition-colors hover:opacity-80"
                          >
                            <div
                              className={`w-1 shrink-0 rounded-l bg-${labColorMap.get(campania.marca) || 'gray-400'}`}
                            />
                            <div
                              className={`flex-1 px-2 py-1.5 rounded-r bg-${labColorMap.get(campania.marca) || 'gray-400'}/10`}
                            >
                              <span className="text-xs leading-tight block truncate font-medium">
                                {campania.nombre_campania}
                              </span>
                              <span className="text-[10px] text-muted-foreground block">
                                {campania.marca} · {campania.estado_envio}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })()
              ) : selectedCampania ? (
                <div className="space-y-4">
                  {/* Header: nombre + tipo envío badge + menú */}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-base leading-tight">{selectedCampania.nombre_campania}</h3>
                    <div className="flex items-center gap-1 shrink-0">
                      {selectedCampania.tipo_envio && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-gray-300 dark:border-gray-600 text-muted-foreground">
                          {selectedCampania.tipo_envio}
                        </Badge>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => window.open(`/multichannel/campania/${selectedCampania.id}/editar`, '_blank')}>
                            <Pencil className="h-3.5 w-3.5 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDuplicarDialogOpen(true)}>
                            <Copy className="h-3.5 w-3.5 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setEstadosForm({
                              estado_contenido: selectedCampania.estado_contenido,
                              estado_envio: selectedCampania.estado_envio,
                              estado_reporte: selectedCampania.estado_reporte,
                            })
                            setEstadosModalOpen(true)
                          }}>
                            <RefreshCw className="h-3.5 w-3.5 mr-2" />
                            Cambiar estados
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {/* Badges de estado */}
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: 'Contenido', value: selectedCampania.estado_contenido, green: 'finalizado' },
                      { label: 'Envío', value: selectedCampania.estado_envio, green: 'enviado' },
                      { label: 'Reporte', value: selectedCampania.estado_reporte, green: 'generado' },
                    ].map(({ label, value, green }) => {
                      const isGreen = value.toLowerCase() === green
                      return (
                        <Badge
                          key={label}
                          variant="outline"
                          className={isGreen
                            ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-transparent'
                            : 'border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400 bg-transparent'
                          }
                        >
                          {label}: {value}
                        </Badge>
                      )
                    })}
                  </div>

                  {/* Detalles */}
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <div
                        className={`w-3 h-3 rounded-sm shrink-0 bg-${labColorMap.get(selectedCampania.marca) || 'gray-400'}`}
                      />
                      <span className="font-medium">{selectedCampania.marca}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4 shrink-0" />
                      <span>Target: {selectedCampania.target}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Send className="h-4 w-4 shrink-0" />
                      <span>Remitente: {selectedCampania.remitente}</span>
                    </div>
                    {selectedCampania.fecha_publicacion && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CalendarDays className="h-4 w-4 shrink-0" />
                        <span>
                          {format(new Date(selectedCampania.fecha_publicacion.split('T')[0] + 'T12:00:00'), "d 'de' MMMM, yyyy", { locale: es })}
                          {selectedCampania.hora && (
                            <span className="ml-1.5 text-foreground font-medium">{selectedCampania.hora}</span>
                          )}
                        </span>
                      </div>
                    )}
                    {selectedCampania.formato && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Layers className="h-4 w-4 shrink-0" />
                        <span>Formato: {selectedCampania.formato}</span>
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="flex gap-2">
                    {selectedCampania.estado_reporte === 'Generado' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          const isWhatsAppOnly = WHATSAPP_TIPOS.includes(selectedCampania.tipo) && selectedCampania.tipo !== 'Email + Whatsapp'
                          const route = isWhatsAppOnly
                            ? `/multichannel/reporte-whatsapp/${selectedCampania.id}`
                            : `/multichannel/reporte/${selectedCampania.id}`
                          window.open(route, '_blank')
                        }}
                      >
                        <FileBarChart className="h-3.5 w-3.5 mr-1.5" />
                        Ver Reporte
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        disabled
                      >
                        <FileBarChart className="h-3.5 w-3.5 mr-1.5" />
                        Reporte pendiente
                      </Button>
                    )}
                    {selectedCampania.tiene_html ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setHtmlPreviewOpen(true)}
                      >
                        <Code className="h-3.5 w-3.5 mr-1.5" />
                        Ver HTML
                      </Button>
                    ) : !WHATSAPP_TIPOS.includes(selectedCampania.tipo) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 border-amber-400 text-amber-600 dark:border-amber-600 dark:text-amber-400"
                        disabled
                      >
                        <Code className="h-3.5 w-3.5 mr-1.5" />
                        Sin HTML
                      </Button>
                    ) : null}
                    {WHATSAPP_TIPOS.includes(selectedCampania.tipo) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => setWaPreviewOpen(true)}
                      >
                        <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                        Ver WhatsApp
                      </Button>
                    )}
                  </div>

                  {/* Preview del reporte (Email o WhatsApp) */}
                  {(reportePreview || reporteWhatsAppPreview) && (() => {
                    // Usar campania_id del reporte (puede diferir si hay redirección)
                    const reporteCampaniaId = reportePreview?.campania_id ?? reporteWhatsAppPreview?.campania_id ?? selectedCampania.id
                    const cat = categorizaciones?.[String(reporteCampaniaId)]
                    const calColorMap: Record<string, string> = {
                      'A+': 'bg-emerald-500 text-white',
                      'A': 'bg-sky-500 text-white',
                      'B': 'bg-amber-400 text-white',
                      'C': 'bg-rose-500 text-white',
                    }
                    const catHexMap: Record<string, string> = {
                      'A+': '#10b981',
                      'A': '#0ea5e9',
                      'B': '#fbbf24',
                      'C': '#f43f5e',
                    }

                    let metricCards: { label: string; value: number; tasa: string | null; categoria: string | null; fullWidth?: boolean }[]

                    if (reporteWhatsAppPreview) {
                      const r = reporteWhatsAppPreview
                      const tasaEntrega = r.base > 0 ? ((r.entrega / r.base) * 100).toFixed(1) : '0.0'
                      const tasaAperturas = r.entrega > 0 ? ((r.leidos / r.entrega) * 100).toFixed(1) : '0.0'
                      const tasaClics = r.entrega > 0 ? ((r.clics / r.entrega) * 100).toFixed(1) : '0.0'
                      metricCards = [
                        { label: 'Base', value: r.base, tasa: null, categoria: null },
                        { label: 'Entrega', value: r.entrega, tasa: tasaEntrega, categoria: r.categoria_entrega ?? null },
                        { label: 'Aperturas', value: r.leidos, tasa: tasaAperturas, categoria: r.categoria_aperturas ?? null },
                        { label: 'Clics', value: r.clics, tasa: tasaClics, categoria: r.categoria_clics ?? null },
                      ]
                    } else {
                      const r = reportePreview!
                      const tasaEntrega = r.base > 0 ? ((r.entrega / r.base) * 100).toFixed(1) : '0.0'
                      const tasaAperturas = r.entrega > 0 ? ((r.aperturas_unicas / r.entrega) * 100).toFixed(1) : '0.0'
                      const tasaClics = r.entrega > 0 ? ((r.clics_unicos / r.entrega) * 100).toFixed(1) : '0.0'
                      metricCards = [
                        { label: 'Base', value: r.base, tasa: null, categoria: null },
                        { label: 'Entrega', value: r.entrega, tasa: tasaEntrega, categoria: r.categoria_entrega },
                        { label: 'Aperturas', value: r.aperturas_unicas, tasa: tasaAperturas, categoria: r.categoria_aperturas },
                        { label: 'Clics únicos', value: r.clics_unicos, tasa: tasaClics, categoria: r.categoria_clics },
                      ]
                    }

                    return (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Resumen del Reporte</span>
                          {cat && (
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${calColorMap[cat.calificacion] || 'bg-gray-400 text-white'}`}>
                                {cat.calificacion}
                              </span>
                              <span className="text-xs text-muted-foreground">{cat.score_final}/100</span>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {metricCards.map(({ label, value, tasa, categoria, fullWidth }) => {
                            const hex = categoria ? catHexMap[categoria] || '#858796' : '#858796'
                            return (
                              <div
                                key={label}
                                className={`rounded-md px-2.5 py-2 border border-gray-200 dark:border-gray-700 overflow-hidden relative ${fullWidth ? 'col-span-2' : ''}`}
                                style={{
                                  background: `linear-gradient(to right, ${hex}18 0%, ${hex}08 40%, transparent 70%)`,
                                }}
                              >
                                <div className="flex items-center justify-between relative">
                                  <span className="text-[10px] text-muted-foreground">{label}</span>
                                  {categoria ? (
                                    <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[8px] font-bold ${calColorMap[categoria] || 'bg-gray-400 text-white'}`}>
                                      {categoria}
                                    </span>
                                  ) : label === 'Base' ? (
                                    <Database className="h-3.5 w-3.5 text-gray-400" />
                                  ) : null}
                                </div>
                                <div className="relative">
                                  <span className="text-sm font-bold text-foreground">{value.toLocaleString()}</span>
                                  {tasa && <span className="text-[10px] text-muted-foreground ml-1">{tasa}%</span>}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              ) : (
                <div className="text-center py-16 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Selecciona una campaña del calendario</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de HTML Preview */}
      <HtmlPreviewModal
        campaniaId={selectedCampania?.id ?? null}
        nombre={selectedCampania?.nombre_campania ?? ''}
        open={htmlPreviewOpen}
        onClose={() => setHtmlPreviewOpen(false)}
      />

      {/* Modal de WhatsApp Preview */}
      <WhatsAppPreviewModal
        campaniaId={selectedCampania?.id ?? null}
        nombre={selectedCampania?.nombre_campania ?? ''}
        open={waPreviewOpen}
        onClose={() => setWaPreviewOpen(false)}
      />

      {/* Modal de Cambiar Estados */}
      <Dialog open={estadosModalOpen} onOpenChange={setEstadosModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar estados</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {[
              { label: 'Contenido', key: 'estado_contenido' as const, done: 'Finalizado', options: filtersData?.estados_contenido },
              { label: 'Envío', key: 'estado_envio' as const, done: 'Enviado', options: filtersData?.estados_envio },
              { label: 'Reporte', key: 'estado_reporte' as const, done: 'Generado', options: filtersData?.estados_reporte },
            ].map(({ label, key, done, options }) => {
              const isDone = estadosForm[key].toLowerCase() === done.toLowerCase()
              return (
                <div key={key} className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    {isDone && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                    <label className={`text-sm font-medium ${isDone ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>{label}</label>
                  </div>
                  <Select value={estadosForm[key]} onValueChange={(v) => setEstadosForm(prev => ({ ...prev, [key]: v }))}>
                    <SelectTrigger className={`h-9 ${isDone ? 'border-emerald-500/50' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {options?.map((e) => (
                        <SelectItem key={e.id} value={e.nombre}>{e.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )
            })}
            <Button
              className="w-full bg-gray-900 hover:bg-black text-white dark:bg-white dark:hover:bg-gray-200 dark:text-gray-900"
              onClick={async () => {
                if (!selectedCampania) return
                try {
                  await updateCampania.mutateAsync({
                    id: selectedCampania.id,
                    data: estadosForm,
                  })
                  setSelectedCampania(prev => prev ? { ...prev, ...estadosForm } : null)
                  setEstadosModalOpen(false)
                  toast({ title: 'Estados actualizados' })
                } catch {
                  toast({ title: 'Error al actualizar estados', variant: 'destructive' })
                }
              }}
              disabled={updateCampania.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {updateCampania.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog duplicar campaña */}
      <Dialog open={duplicarDialogOpen} onOpenChange={setDuplicarDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Duplicar Campaña
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Selecciona cómo quieres duplicar <strong>{selectedCampania?.nombre_campania}</strong>
          </p>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              onClick={() => {
                setDuplicarDialogOpen(false)
                if (selectedCampania) navigate(`/multichannel/campania/${selectedCampania.id}/duplicar?tipo=normal`)
              }}
              className="flex flex-col gap-2 rounded-lg border-2 border-transparent p-4 text-left hover:border-teal-500 hover:bg-teal-500/5 transition-colors"
            >
              <div className="flex items-center gap-2 font-medium text-sm">
                <Copy className="h-4 w-4 text-teal-500" />
                Normal
              </div>
              <p className="text-xs text-muted-foreground">
                Conserva configuración. Asunto, HTML y fechas en blanco.
              </p>
            </button>
            <button
              onClick={() => {
                setDuplicarDialogOpen(false)
                if (selectedCampania) navigate(`/multichannel/campania/${selectedCampania.id}/duplicar?tipo=reenvio`)
              }}
              className="flex flex-col gap-2 rounded-lg border-2 border-transparent p-4 text-left hover:border-purple-500 hover:bg-purple-500/5 transition-colors"
            >
              <div className="flex items-center gap-2 font-medium text-sm">
                <Send className="h-4 w-4 text-purple-500" />
                Reenvío
              </div>
              <p className="text-xs text-muted-foreground">
                Conserva HTML y asunto. Fechas y estado de envío en blanco.
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
